/**
 * Keyboard and mouse for a third-person MMORPG.
 *
 * The model, in Chris's words (2026-09-05):
 *
 * - **The cursor is visible by default.** It is how you click a target, press a bar slot and
 *   work a panel. Nothing captures it while you are just standing there.
 * - **Right-click and HOLD is the only way to orbit.** While the right button is down the
 *   pointer is captured, the cursor is hidden, and mouse movement rotates the camera *and* the
 *   body together. Release and the cursor comes back where it was. Left-drag does nothing to
 *   the camera.
 * - **Both buttons held moves forward.**
 * - **A/D turn the body** (the camera follows behind) when the right button is not held, and
 *   **strafe** while it is. W after any camera offset swings the body onto the camera's yaw.
 * - **The wheel zooms** between a close shoulder distance and a far limit. No first-person stop.
 *
 * Two yaws, not one. `bodyYaw` is what the simulation and the wire see; `camYaw`/`camPitch` are
 * the client's own and never leave the machine. `main.ts` reads all three.
 *
 * There is no touch path. Mobile was dropped in the 2026-09-05 pivot (plan §1e).
 */

import { ABILITY_BUTTONS, Buttons } from '@stillsun/shared';

export interface InputSample {
  moveX: number;
  moveY: number;
  /** BODY yaw — the only look number the server is ever told about (PROTOCOL-V3 §1.2). */
  yaw: number;
  buttons: number;
  /** The selected target, 0 for none. A request; the server validates it every tick. */
  targetId: number;
}

const MOUSE_SENS = 0.0022; // rad per px
/** How fast A/D swing the body when the right button is not held (rad/s). ~170°/s, WoW-ish. */
const TURN_RATE = 3;
/** Boom elevation limits. The lower stop keeps the camera off the ground before the boom
 * collision trace ever has to; the upper one stops it going over the top of the head. */
const PITCH_MIN = -0.26; // ~-15°, looking slightly up at the character
const PITCH_MAX = 1.31; // ~75°, near top-down

/** Boom length limits, in metres. `CAM_DIST_MIN` is a close shoulder; there is no first person. */
export const CAM_DIST_MIN = 1.5;
export const CAM_DIST_MAX = 12;
const CAM_DIST_DEFAULT = 5.5;
/** One wheel notch, in metres of boom. */
const ZOOM_STEP = 0.6;
/**
 * Wheel hysteresis, both gates. A trackpad emits a stream of tiny deltas; a mouse emits one
 * large one every notch. Requiring *both* an accumulated delta and a settled cooldown means a
 * trackpad flick zooms smoothly instead of skipping the whole range, and a mouse notch still
 * registers on the first event.
 */
const WHEEL_DELTA_GATE = 20;
const WHEEL_COOLDOWN_MS = 40;

/** A tap-latched button stays set this long so at least one fixed-step command carries it. */
const PULSE_MS = 120;

/**
 * A right-click shorter than this, with the pointer barely moved, is a *click* (talk to the
 * person under the cursor) rather than the start of an orbit.
 */
const RIGHT_CLICK_MS = 220;
const RIGHT_CLICK_SLOP_PX = 6;

/** Protocol v3's bits (docs/PROTOCOL-V3.md). Named here so the sampler reads plainly. */
const BTN_JUMP = Buttons.Jump;
const BTN_SPRINT = Buttons.Sprint;
const BTN_INTERACT = Buttons.Interact;
/** A toggle, not a hold: the rising edge stands you up or sits you down. */
const BTN_SIT = Buttons.Sit;
/** Also a toggle — the rising edge turns swinging at your target on or off. */
const BTN_AUTO = Buttons.AutoAttack;

export interface InputOptions {
  canvas: HTMLCanvasElement;
  /** Enter pressed while playing — the host opens the chat box. */
  onChatOpen: () => void;
  /** Escape pressed. The host clears the target first, then closes panels. */
  onEscape: () => void;
  /** Tab / Shift-Tab — cycle the target ring outward or inward. */
  onCycleTarget: (backwards: boolean) => void;
  /** E pressed (edge). */
  onInteract: () => void;
  /** B pressed (I also works), or the bar's Bag button. */
  onInventory: () => void;
  /** T pressed while a UI panel is open — Take All in the loot panel. */
  onTakeAll: () => void;
  /** F8 — open the feedback terminal. */
  onFeedback: () => void;
  /** An ability key went down, slot 0..5. The bit is latched regardless; this is so the bar
   * can start its cooldown sweep on the press frame rather than a round-trip later. */
  onAbility: (slot: number) => void;
  /** Left-click on the world, in CSS pixels relative to the viewport. Selects or clears. */
  onPick: (clientX: number, clientY: number) => void;
  /** A right-click that was a click and not the start of an orbit — talk to whoever is there. */
  onRightPick: (clientX: number, clientY: number) => void;
  /** Cursor moved over the world with nothing held; drives the hover highlight. */
  onHover: (clientX: number, clientY: number) => void;
}

export class InputController {
  /** Where the *body* faces. The one look number that goes on the wire. */
  bodyYaw = 0;
  /** Where the *camera* faces. Client-only: the server never sees it. */
  camYaw = 0;
  /** Boom elevation, radians. Positive looks down at the character. Client-only. */
  camPitch = 0.32;
  /** Boom length in metres, driven by the wheel. Client-only. */
  camDist = CAM_DIST_DEFAULT;

  /** While true, keyboard movement is ignored (chat box has focus). */
  chatMode = false;
  /**
   * While true a UI panel (loot, inventory, feedback, hub) is up. Movement is deliberately
   * *not* gated on this — a player must be able to walk away from an open loot box — and
   * neither is the camera any more: the cursor was always free, so a panel takes nothing away.
   */
  uiOpen = false;
  /** While true, abilities and auto-attack are suppressed (a panel owns the keyboard). */
  combatSuppressed = false;
  /** Settings › sensitivity. Multiplies every mouse look delta. */
  sensitivity = 1;
  /** Settings › invert. Flips the vertical while orbiting. */
  invertPitch = false;
  /** True once the player has entered the world. */
  active = false;
  /** While true, all look input is ignored (death cam). */
  lookLocked = false;
  /**
   * X sits. This is the client's own latch so the crowd rig can pose the body immediately;
   * the *bit* is a rising-edge toggle the server owns, so it is pulsed on the press and not
   * held every tick.
   */
  sitting = false;
  /** The selected target id, written by the host's targeting code and sent on every command. */
  targetId = 0;
  /** True while the right button is held: the pointer is captured and the camera is orbiting. */
  orbiting = false;

  private readonly keys = new Set<string>();
  private readonly opts: InputOptions;

  /** Mouse buttons, tracked so "both held" can mean forward. */
  private leftDown = false;
  private rightDown = false;

  /** Latched (tap) buttons: bit -> expiry timestamp. */
  private readonly pulses = new Map<number, number>();

  /** Wheel hysteresis state (see WHEEL_DELTA_GATE). */
  private wheelAccum = 0;
  private wheelAt = 0;

  /** Right-button press bookkeeping, for the click-versus-orbit split. */
  private rightAt = 0;
  private rightX = 0;
  private rightY = 0;
  private rightMoved = 0;
  /** True when the left button was already down as the right went down — the run gesture. */
  private rightWithLeft = false;

  private readonly disposers: Array<() => void> = [];

  constructor(opts: InputOptions) {
    this.opts = opts;
    this.bindKeyboard();
    this.bindMouse();
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers.length = 0;
  }

  private add<E extends Event = Event>(
    target: Window | Document | HTMLElement,
    type: string,
    fn: (ev: E) => void,
    options?: AddEventListenerOptions,
  ): void {
    const handler = fn as unknown as EventListener;
    target.addEventListener(type, handler, options);
    this.disposers.push(() => {
      target.removeEventListener(type, handler, options);
    });
  }

  /**
   * Fullscreen on deploy. Best-effort and gated on a user gesture; a failure here must never
   * stop the player entering the world. (The orientation lock that used to sit beside this
   * went with the touch layer.)
   */
  async enterFullscreen(): Promise<void> {
    try {
      const el = document.documentElement;
      if (!document.fullscreenElement && typeof el.requestFullscreen === 'function') {
        await el.requestFullscreen({ navigationUI: 'hide' });
      }
    } catch {
      /* denied or unsupported */
    }
  }

  /** Drops the orbit and any captured pointer — used when a death cam or the hub takes over. */
  endOrbit(): void {
    if (!this.orbiting) return;
    this.orbiting = false;
    this.rightDown = false;
    if (document.pointerLockElement) document.exitPointerLock();
  }

  // ------------------------------------------------------------------ keyboard

  private bindKeyboard(): void {
    this.add(window, 'keydown', (ev: KeyboardEvent) => {
      if (ev.code === 'Escape') {
        this.keys.clear();
        this.opts.onEscape();
        return;
      }
      // F8 opens the feedback terminal from anywhere, including the hub — the whole point is
      // that it is reachable the moment something looks wrong.
      if (ev.code === 'F8') {
        ev.preventDefault();
        this.opts.onFeedback();
        return;
      }
      // A focused text field owns the keyboard outright: no game bindings, no preventDefault,
      // so typing "wasd" types "wasd".
      const target = ev.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (this.chatMode) return;
      if (!this.active) return;
      // While the game owns the keyboard, no Ctrl/Cmd letter or digit chord reaches the browser
      // (Ctrl+W/T are browser-reserved and cannot be cancelled; everything else can).
      // 2026-09-04 owner report: Ctrl+A highlighted the whole HUD mid-raid.
      if ((ev.ctrlKey || ev.metaKey) && /^(Key[A-Z]|Digit[0-9])$/.test(ev.code)) {
        ev.preventDefault();
      }
      // Tab repeats while held; handle it before the repeat guard so a held Tab keeps cycling
      // the ring the way it does in every MMORPG.
      if (ev.code === 'Tab') {
        ev.preventDefault();
        this.opts.onCycleTarget(ev.shiftKey);
        return;
      }
      // While a UI panel is up, movement keeps working — a player must be able to walk away
      // from an open loot box — but abilities and chat are out, and only the panel verbs get
      // their one-shot actions.
      if (this.uiOpen) {
        if (!ev.repeat) {
          if (ev.code === 'KeyE') this.opts.onInteract();
          else if (ev.code === 'KeyI' || ev.code === 'KeyB') this.opts.onInventory();
          else if (ev.code === 'KeyT') this.opts.onTakeAll();
        }
        if (PANEL_MOVE_KEYS.has(ev.code)) {
          ev.preventDefault();
          this.keys.add(ev.code);
        }
        return;
      }
      if (ev.code === 'Enter' || ev.code === 'NumpadEnter') {
        ev.preventDefault();
        this.opts.onChatOpen();
        return;
      }
      if (ev.repeat) return;
      // Stop the page scrolling / space-activating focused buttons.
      if (GAME_KEYS.has(ev.code)) ev.preventDefault();
      // Abilities fire on the press edge only — the server casts on the button edge, so a held
      // key must never re-cast every tick.
      const slot = ABILITY_KEYS.get(ev.code);
      if (slot !== undefined) this.ability(slot);
      // X sits, and ` turns auto-attack on or off. Both bits are toggles the server owns,
      // so both are pulsed on the press edge rather than held.
      if (ev.code === 'KeyX') {
        this.sitting = !this.sitting;
        this.pulse(BTN_SIT);
      }
      if (ev.code === 'Backquote') this.pulse(BTN_AUTO);
      if (CLEARS_SIT.has(ev.code)) this.sitting = false;
      // Panel verbs fire on the press edge. E also stays held for Buttons.Interact so the
      // server keeps seeing the hold, but the UI action must happen exactly once.
      if (ev.code === 'KeyE') this.opts.onInteract();
      else if (ev.code === 'KeyI' || ev.code === 'KeyB') this.opts.onInventory();
      // W is the "get behind me" key: after any camera offset, pressing forward swings the
      // body onto the camera's yaw so you run where you are looking.
      if (ev.code === 'KeyW' || ev.code === 'ArrowUp') this.bodyYaw = this.camYaw;
      this.keys.add(ev.code);
    });

    this.add(window, 'keyup', (ev: KeyboardEvent) => {
      this.keys.delete(ev.code);
    });

    this.add(window, 'blur', () => {
      this.keys.clear();
      this.pulses.clear();
      this.leftDown = false;
      this.rightDown = false;
      this.endOrbit();
    });
  }

  /** Latch a button bit for PULSE_MS so a tap survives to the next fixed step. */
  private pulse(bit: number): void {
    this.pulses.set(bit, performance.now() + PULSE_MS);
  }

  /**
   * Ability press edge, slot 0..5. Latched like a tap (the server casts on the button edge, so
   * one command carrying the bit is exactly right) and reported to the host so the bar's sweep
   * starts immediately instead of waiting for the next S2C.Progress.
   */
  private ability(slot: number): void {
    if (this.combatSuppressed) return;
    this.pulse(ABILITY_BUTTONS[slot] ?? ABILITY_BUTTONS[0]!);
    this.opts.onAbility(slot);
  }

  /**
   * The same press, from a click on the bar rather than from a number key (ui/raidbar.ts). It
   * goes through `ability()` on purpose: click-to-cast and key-to-cast have to latch the same
   * button bit and be suppressed by the same things, or the two disagree about what a cast is.
   */
  pressAbility(slot: number): void {
    if (slot < 0 || slot >= ABILITY_BUTTONS.length) return;
    this.ability(slot);
  }

  // --------------------------------------------------------------------- mouse

  private bindMouse(): void {
    this.add(document, 'pointerlockchange', () => {
      // Losing the lock (alt-tab, the browser's own Escape) ends the orbit, but the right
      // button may still be physically down — `mouseup` clears that either way.
      if (document.pointerLockElement !== this.opts.canvas && this.orbiting) {
        this.orbiting = false;
      }
    });

    this.add(document, 'mousemove', (ev: MouseEvent) => {
      if (this.orbiting) {
        // Captured: `movementX/Y` are the only meaningful numbers, and they keep arriving past
        // the edge of the screen. If the lock was refused (Chrome rate-limits a re-lock that
        // follows an exit too closely) the same deltas still work unlocked, so a fast RMB tap
        // never leaves the camera dead — it just leaves the cursor visible for that spin.
        // Counted in *pixels*, so the click-versus-orbit slop below is a real screen distance
        // and not a number of radians that happens to look like one.
        this.rightMoved += Math.abs(ev.movementX) + Math.abs(ev.movementY);
        this.applyLook(ev.movementX * MOUSE_SENS, ev.movementY * MOUSE_SENS);
        return;
      }
      if (!this.active || this.uiOpen) return;
      this.opts.onHover(ev.clientX, ev.clientY);
    });

    this.add(this.opts.canvas, 'mousedown', (ev: MouseEvent) => {
      if (!this.active || this.chatMode) return;
      if (ev.button === 0) {
        this.leftDown = true;
        // Left-click is a *selection*, not a camera gesture. The pick happens on the press so
        // it feels immediate; a drag from here does nothing at all.
        //
        // Never while orbiting: the left button coming down on top of a held right button is
        // the run-forward gesture, and the pointer is captured, so `clientX/Y` are frozen
        // wherever the cursor was when the lock engaged. Picking there would trace a stale ray
        // into empty world and silently drop the player's target every time they ran forward.
        if (!this.orbiting) this.opts.onPick(ev.clientX, ev.clientY);
      } else if (ev.button === 2) {
        ev.preventDefault();
        this.rightDown = true;
        this.rightAt = performance.now();
        this.rightX = ev.clientX;
        this.rightY = ev.clientY;
        this.rightMoved = 0;
        this.rightWithLeft = this.leftDown;
        this.beginOrbit();
      }
    });

    // Tracked on the window as well, so a button released outside the canvas is not left stuck.
    this.add(window, 'mouseup', (ev: MouseEvent) => {
      if (ev.button === 0) {
        this.leftDown = false;
        return;
      }
      if (ev.button !== 2) return;
      const wasOrbiting = this.orbiting;
      this.rightDown = false;
      this.endOrbit();
      // A short right-press that barely moved is a click on whatever is under the cursor —
      // talking to a person — rather than a flick of the camera. A press with the left button
      // held on either edge is the run-forward gesture and never a click.
      const ranForward = this.rightWithLeft || this.leftDown;
      this.rightWithLeft = false;
      if (
        this.active &&
        !this.chatMode &&
        wasOrbiting &&
        !ranForward &&
        performance.now() - this.rightAt <= RIGHT_CLICK_MS &&
        this.rightMoved <= RIGHT_CLICK_SLOP_PX
      ) {
        this.opts.onRightPick(this.rightX, this.rightY);
      }
    });

    // The wheel zooms the boom. Two gates (see WHEEL_DELTA_GATE): a trackpad's stream of tiny
    // deltas has to accumulate *and* the cooldown has to have expired before a step is taken.
    this.add(
      window,
      'wheel',
      (ev: WheelEvent) => {
        if (!this.active || this.chatMode || this.uiOpen) return;
        ev.preventDefault();
        // Lines and pages come through as much smaller numbers than pixels.
        const scale = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? 100 : 1;
        this.wheelAccum += ev.deltaY * scale;
        const now = performance.now();
        if (Math.abs(this.wheelAccum) < WHEEL_DELTA_GATE) return;
        if (now - this.wheelAt < WHEEL_COOLDOWN_MS) return;
        this.wheelAt = now;
        const steps = Math.max(1, Math.min(4, Math.round(Math.abs(this.wheelAccum) / 100)));
        this.camDist = clampDist(this.camDist + Math.sign(this.wheelAccum) * ZOOM_STEP * steps);
        this.wheelAccum = 0;
      },
      { passive: false },
    );

    this.add(this.opts.canvas, 'contextmenu', (ev: Event) => {
      ev.preventDefault();
    });
  }

  /** Capture the pointer and hide the cursor for as long as the right button is down. */
  private beginOrbit(): void {
    this.orbiting = true;
    try {
      const r = this.opts.canvas.requestPointerLock() as unknown;
      // A rejection (Chrome rate-limits a re-lock straight after an exit) is not fatal: the
      // orbit still runs off the raw deltas, only with the cursor left on screen.
      if (r instanceof Promise) r.catch(() => undefined);
    } catch {
      /* older Safari throws instead of rejecting */
    }
  }

  private applyLook(dYaw: number, dPitch: number): void {
    if (this.lookLocked) return;
    const s = this.sensitivity;
    // Positive yaw turns left, so moving the mouse right must decrease yaw.
    this.camYaw = wrapPi(this.camYaw - dYaw * s);
    // While the right button is held the body goes with the camera — that is the whole of
    // "spin the character with the mouse".
    this.bodyYaw = this.camYaw;
    const pitch = this.invertPitch ? -dPitch : dPitch;
    this.camPitch = clamp(this.camPitch + pitch * s, PITCH_MIN, PITCH_MAX);
  }

  /**
   * Nudges the look angles directly, in radians (+yaw turns left, +pitch looks up). Ability
   * shove and camera kick go through here rather than writing the camera, so the yaw the
   * server receives is exactly what the player is seeing.
   */
  addLook(dYaw: number, dPitch: number): void {
    this.camYaw = wrapPi(this.camYaw + dYaw);
    this.bodyYaw = wrapPi(this.bodyYaw + dYaw);
    this.camPitch = clamp(this.camPitch - dPitch, PITCH_MIN, PITCH_MAX);
  }

  // -------------------------------------------------------------------- sample

  private held(...codes: string[]): boolean {
    for (const c of codes) if (this.keys.has(c)) return true;
    return false;
  }

  /**
   * Advances the parts of the camera that run on a clock rather than an event: A/D turning
   * while the right button is *not* held. Called once a frame from the host, before
   * `sampleInput`, so the yaw the command carries is the yaw the frame was drawn at.
   */
  updateTurn(dt: number): void {
    if (!this.active || this.chatMode || this.lookLocked || this.orbiting) return;
    let turn = 0;
    if (this.held('KeyA', 'ArrowLeft')) turn += 1;
    if (this.held('KeyD', 'ArrowRight')) turn -= 1;
    if (turn === 0) return;
    const d = turn * TURN_RATE * dt;
    this.bodyYaw = wrapPi(this.bodyYaw + d);
    // The camera follows the body round rather than being left behind it.
    this.camYaw = wrapPi(this.camYaw + d);
  }

  sampleInput(): InputSample {
    let moveX = 0;
    let moveY = 0;
    let buttons = 0;

    // Movement/sprint/jump stay live even while a UI panel is open — only abilities are gated,
    // via `combatSuppressed`, so a loot box can never pin the player in place.
    if (!this.chatMode && this.active) {
      if (this.held('KeyW', 'ArrowUp')) moveY += 1;
      if (this.held('KeyS', 'ArrowDown')) moveY -= 1;
      // A/D strafe only while orbiting; otherwise they turn the body (see `updateTurn`).
      if (this.orbiting) {
        if (this.held('KeyD', 'ArrowRight')) moveX += 1;
        if (this.held('KeyA', 'ArrowLeft')) moveX -= 1;
      }
      // Both mouse buttons held is "run forward", the oldest gesture in the genre.
      if (this.leftDown && this.rightDown) moveY += 1;
      if (this.held('ShiftLeft', 'ShiftRight')) buttons |= BTN_SPRINT;
      if (this.held('Space')) buttons |= BTN_JUMP;
      if (this.held('KeyE')) buttons |= BTN_INTERACT;
    }

    // Moving at all stands you back up. The server does the same on its side from the move
    // vector, so no bit has to be sent to say so.
    if (moveX !== 0 || moveY !== 0 || (buttons & BTN_JUMP) !== 0) this.sitting = false;

    // Latched taps (ability presses).
    if (this.pulses.size > 0) {
      const now = performance.now();
      for (const [bit, until] of [...this.pulses]) {
        if (now >= until) this.pulses.delete(bit);
        else buttons |= bit;
      }
    }

    if (!this.active || this.chatMode || this.combatSuppressed) {
      // Never cast through the chat box, the hub or an open panel.
      buttons &= ~BTN_AUTO;
      for (const b of ABILITY_BUTTONS) buttons &= ~b;
    }

    // Normalise so diagonals are not faster (stepPlayer also clamps, but the wire format
    // quantises to int8 and we want the sent value to be sane).
    const len = Math.hypot(moveX, moveY);
    if (len > 1) {
      moveX /= len;
      moveY /= len;
    }

    return { moveX, moveY, yaw: this.bodyYaw, buttons, targetId: this.targetId };
  }
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

const clampDist = (v: number): number =>
  v < CAM_DIST_MIN ? CAM_DIST_MIN : v > CAM_DIST_MAX ? CAM_DIST_MAX : v;

function wrapPi(a: number): number {
  let v = a;
  if (v > Math.PI) v -= Math.PI * 2;
  if (v < -Math.PI) v += Math.PI * 2;
  return v;
}

/** Digit (and numpad) key -> ability slot 0..5. The bar has six slots in the pivot build. */
const ABILITY_KEYS = new Map<string, number>([
  ['Digit1', 0],
  ['Numpad1', 0],
  ['Digit2', 1],
  ['Numpad2', 1],
  ['Digit3', 2],
  ['Numpad3', 2],
  ['Digit4', 3],
  ['Numpad4', 3],
  ['Digit5', 4],
  ['Numpad5', 4],
  ['Digit6', 5],
  ['Numpad6', 5],
]);

/** Anything that means "I am not sitting any more". */
const CLEARS_SIT = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
]);

const GAME_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyE',
  'KeyI',
  'KeyB',
  'KeyT',
  'KeyX',
  'Digit1',
  'Digit2',
  'Digit3',
  'Digit4',
  'Digit5',
  'Digit6',
  'Space',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  // Kept so focus never walks off into the HUD, even though Tab now cycles targets.
  'Tab',
]);

/**
 * Held-movement keys that must keep reaching `this.keys` while a UI panel is open — WASD,
 * arrows, sprint, jump. Abilities are deliberately excluded; those are gated by
 * `combatSuppressed` in sampleInput().
 */
const PANEL_MOVE_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ShiftLeft',
  'ShiftRight',
  'Space',
]);
